import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HorizontalEqualLayout, HorizontalLayout, HoverVerticalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { Body2, Body3, Point3, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { MediaPost } from '../../common/data/MediaData';
import { fetchLatestMediaCenterMarketings, fetchTabManagers } from '../../common/firebase/FirestoreService';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useDeviceType } from '../../hooks/useDeviceType';

const IntroduceSection6 = () => {
  const { t } = useTranslation();
  const { horizontal: padding } = useResponsivePadding();
  const { isMobile, isDesktop, isPad, screenWidth } = useDeviceType();
  const [mediaPosts, setMediaPosts] = useState<MediaPost[]>([]);
  const navigate = useNavigate();
  const { trackButtonClick } = useAnalytics();
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  const MarqueeContainer = styled.div`
  overflow: hidden;
  width: 100%;
  // white-space: nowrap;
`;

  const MarqueeContent = styled.div<{ isDisabled?: boolean }>`
display: inline-flex;
animation: ${({ isDisabled }) =>
      isDisabled ? "none" : "scrollLeft 10s linear infinite"};

&:hover {
  animation-play-state: ${({ isDisabled }) => (isDisabled ? "running" : "paused")};
}

@keyframes scrollLeft {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-33.333%);
  }
}
`;

  const SlideItem = styled.div`
  width: ${pxToRem(498)};
  margin-right: 32px;
  flex-shrink: 0;
`;

  useEffect(() => {
    const fetchData = async () => {
      const tab = await fetchTabManagers('marketing');
      const tabCodes = tab.map(item => item.tabCode);
      const marketings = await fetchLatestMediaCenterMarketings(['mediaCenter']);
      const allData: MediaPost[] = marketings
        .filter(item => item.marketingType !== 'companyInfo')
        .map((item, index) => ({
          index: index,
          imageUrl: item.representImage ?? '',
          title: item.title,
          content: item.contents,
          date: item.postDate
            ? item.postDate.toISOString().slice(0, 10)
            : '', // 날짜가 null이면 빈 문자열
          type: tab.find((t) => t.tabCode === item.marketingType)?.tabName ?? '', // tab에서 이름 매핑
          attachedFiles: item.attachedFiles,
        }));

      setMediaPosts(allData);
    };

    fetchData();
  }, []);

  return (
    <VerticalLayout style={{ background: 'white', paddingLeft: padding, paddingRight: padding }}>
      <BTBox height={isMobile ? 60 : `${pxToRem(180)}`} />
      <TitleNormalBold fontSize={`${pxToRem(70)}`} color='#000000'>Media</TitleNormalBold>
      <BTBox height={`${pxToRem(57)}`} />
      {isMobile && !isPad ? (
        <VerticalLayout style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}>
          {mediaPosts.map((item, index) => (
            <VerticalLayout>
              <HoverVerticalLayout backgroundColor="#EEEEF1" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Analytics 이벤트를 먼저 전송
                trackButtonClick(`미디어_${index + 1}`, `main-미디어_${index + 1}`, {
                  button_text: t(item.title),
                  button_location: 'div',
                  button_type: 'click',
                  page_name_korean: '메인'
                });
                
                // requestIdleCallback을 사용하여 Analytics 전송 완료 후 페이지 이동
                if (window.requestIdleCallback) {
                  window.requestIdleCallback(() => {
                    navigate(`/promotion?index=${item.index}`);
                  });
                } else {
                  setTimeout(() => {
                    navigate(`/promotion?index=${item.index}`);
                  }, 1000);
                }
              }} style={{
                cursor: 'pointer'
              }} data-analytics-name={`미디어_${index + 1}`} className="media-card">
                <div style={{ width: '100%' }}>
                  <img src={item.imageUrl} style={{
                    width: '100%',
                    aspectRatio: '328 / 198',
                    display: 'block',
                  }} />
                </div>
                <VerticalLayout wrapContents={true} style={{ width: '100%', paddingLeft: '26px', paddingRight: '26px' }}>
                  <BTBox height={24} />
                  <Body2 style={{ fontSize: '18px', lineHeight: '26px', minHeight: '52px' }}>{t(item.title)}</Body2>
                  <BTBox height={16} />
                  <Point3 style={{ color: '#979797' }}>{t(item.date)}</Point3>
                  <BTBox height={24} />
                </VerticalLayout>
              </HoverVerticalLayout>
              <BTBox height={4} />
            </VerticalLayout>
          ))}
        </VerticalLayout>
      ) : isMobile && isPad ? (
        screenWidth > 880 ? (
          <HorizontalEqualLayout gap={pxToRem(24)} style={{
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            justifyContent: 'flex-start',
          }}>
            {mediaPosts.map((item, index) => (
              <HoverVerticalLayout backgroundColor="#FAFAFA" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Analytics 이벤트를 먼저 전송
                trackButtonClick(`미디어_${index + 1}`, `main-미디어_${index + 1}`, {
                  button_text: t(item.title),
                  button_location: 'div',
                  button_type: 'click',
                  page_name_korean: '메인'
                });
                
                // requestIdleCallback을 사용하여 Analytics 전송 완료 후 페이지 이동
                if (window.requestIdleCallback) {
                  window.requestIdleCallback(() => {
                    navigate(`/promotion?index=${item.index}`);
                  });
                } else {
                  setTimeout(() => {
                    navigate(`/promotion?index=${item.index}`);
                  }, 1000);
                }
              }} style={{
                width: '100%',
                cursor: 'pointer'
              }} data-analytics-name={`미디어_${index + 1}`} className="media-card">
                <img src={item.imageUrl} style={{ width: '100%', aspectRatio: '498 / 300' }} />
                <VerticalLayout wrapContents={true} paddingLeft={pxToRem(30)} paddingTop={pxToRem(43)} paddingRight={pxToRem(43)} paddingBottom={pxToRem(40)}>
                  <Body3 style={{ minHeight: `${pxToRem(80)}` }}>{t(item.title)}</Body3>
                  <BTBox height={pxToRem(20)} />
                  <Point3 style={{ color: '#979797' }}>{t(item.date)}</Point3>
                </VerticalLayout>
              </HoverVerticalLayout>
            ))}
          </HorizontalEqualLayout>
        ) : (
          <HorizontalLayout style={{overflowX: 'auto'}}>
            {mediaPosts.map((item, index) => (
              <HoverVerticalLayout backgroundColor="#FAFAFA" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Analytics 이벤트를 먼저 전송
                trackButtonClick(`미디어_${index + 1}`, `main-미디어_${index + 1}`, {
                  button_text: t(item.title),
                  button_location: 'div',
                  button_type: 'click',
                  page_name_korean: '메인'
                });
                
                // requestIdleCallback을 사용하여 Analytics 전송 완료 후 페이지 이동
                if (window.requestIdleCallback) {
                  window.requestIdleCallback(() => {
                    navigate(`/promotion?index=${item.index}`);
                  });
                } else {
                  // requestIdleCallback이 지원되지 않는 경우 setTimeout 사용
                  setTimeout(() => {
                    navigate(`/promotion?index=${item.index}`);
                  }, 1000);
                }
              }} style={{
                maxWidth: '271px',
                flexShrink: 0,
                cursor: 'pointer',
                ...(index != 0) ? { marginLeft: '16px' } : {}
              }} data-analytics-name={`미디어_${index + 1}`} className="media-card">
                <img src={item.imageUrl} style={{ width: '100%', aspectRatio: '498 / 300' }} />
                <VerticalLayout wrapContents={true} paddingLeft={pxToRem(30)} paddingTop={pxToRem(43)} paddingRight={pxToRem(43)} paddingBottom={pxToRem(40)}>
                  <Body3 style={{ minHeight: `${pxToRem(80)}` }}>{t(item.title)}</Body3>
                  <BTBox height={pxToRem(20)} />
                  <Point3 style={{ color: '#979797' }}>{t(item.date)}</Point3>
                </VerticalLayout>
              </HoverVerticalLayout>
            ))}
          </HorizontalLayout>
        )
      ) : (
        <HorizontalEqualLayout gap={pxToRem(24)} style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}>
          {mediaPosts.map((item, index) => (
            <HoverVerticalLayout backgroundColor="#FAFAFA" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Analytics 이벤트를 먼저 전송
              trackButtonClick(`미디어_${index + 1}`, `main-미디어_${index + 1}`, {
                button_text: t(item.title),
                button_location: 'div',
                button_type: 'click',
                page_name_korean: '메인'
              });
              
              // requestIdleCallback을 사용하여 Analytics 전송 완료 후 페이지 이동
              if (window.requestIdleCallback) {
                window.requestIdleCallback(() => {
                  navigate(`/promotion?index=${item.index}`);
                });
              } else {
                // requestIdleCallback이 지원되지 않는 경우 setTimeout 사용
                setTimeout(() => {
                  navigate(`/promotion?index=${item.index}`);
                }, 1000);
              }
            }} style={{
              width: '100%',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }} data-analytics-name={`미디어_${index + 1}`} className="media-card">
              <img src={item.imageUrl} style={{ width: '100%', aspectRatio: '498 / 300' }} />
              <VerticalLayout wrapContents={true} paddingLeft={pxToRem(30)} paddingTop={pxToRem(43)} paddingRight={pxToRem(43)} paddingBottom={pxToRem(40)}>
                <Body3 style={{ minHeight: `${pxToRem(80)}` }}>{t(item.title)}</Body3>
                <BTBox height={pxToRem(20)} />
                <Point3 style={{ color: '#979797' }}>{t(item.date)}</Point3>
              </VerticalLayout>
            </HoverVerticalLayout>
          ))}
        </HorizontalEqualLayout>
      )}

      <BTBox height={isMobile ? 60 : `${pxToRem(234)}`} />
    </VerticalLayout>
  );
};

export default IntroduceSection6;
